import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

const SITUATIONS = [
  { value: "pre_foreclosure", label: "I'm facing pre-foreclosure" },
  { value: "post_foreclosure_surplus", label: "My home was foreclosed — recover surplus funds" },
  { value: "sell_fast", label: "I want to sell my house fast" },
  { value: "other", label: "Something else — I need guidance" },
];

const TIMELINES = [
  { value: "asap", label: "ASAP (within 7 days)" },
  { value: "30d", label: "Within 30 days" },
  { value: "60_90", label: "60-90 days" },
  { value: "unsure", label: "Not sure yet" },
];

export default function LeadForm({
  compact = false,
  defaultSituation = "",
  testid = "lead-form",
  onSuccess,
}) {
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    property_address: "",
    situation: defaultSituation,
    loan_balance: "",
    estimated_value: "",
    timeline: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.situation) {
      toast.error("Please fill in your name, phone, and situation.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      payload.full_name = form.full_name;
      payload.phone = form.phone;
      payload.situation = form.situation;

      await api.post("/leads", payload);
      setDone(true);
      toast.success("We received your request. An advisor will call you shortly.");
      onSuccess?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid={`${testid}-success`}
        className="rounded-lg bg-white border border-[#E5E3DB] p-8 text-center"
      >
        <CheckCircle2 className="h-10 w-10 text-[#2C5545] mx-auto mb-4" />
        <h3 className="font-display text-2xl text-[#0B1B3D] mb-2">
          Thank you — we've got your request.
        </h3>
        <p className="text-[#64748B] text-sm leading-relaxed">
          A homeowner advocate will contact you within one business hour.
          For urgent situations, please text <span className="font-medium text-[#0B1B3D]">706-506-3860</span>.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid={testid}
      className={`rounded-lg bg-white border border-[#E5E3DB] ${
        compact ? "p-6" : "p-7 lg:p-8"
      } shadow-[0_20px_60px_rgba(11,27,61,0.10)]`}
    >
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="h-4 w-4 text-[#2C5545]" />
        <span className="text-[11px] uppercase tracking-[0.22em] text-[#2C5545] font-medium">
          100% Free & Confidential
        </span>
      </div>
      <h3 className="font-display text-2xl lg:text-3xl text-[#0B1B3D] mb-1">
        Get Your Free Clear Path Relief Plan
      </h3>
      <p className="text-sm text-[#64748B] mb-6">
        No cost. No obligation. Personal review by a licensed advocate.
      </p>

      <div className="grid grid-cols-1 gap-4">
        <div>
          <Label htmlFor="lf-name" className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
            Full Name *
          </Label>
          <Input
            id="lf-name"
            data-testid="lf-full-name"
            value={form.full_name}
            onChange={(e) => upd("full_name", e.target.value)}
            placeholder="Jane Doe"
            className="mt-1.5 h-11"
            required
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="lf-phone" className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
              Phone *
            </Label>
            <Input
              id="lf-phone"
              data-testid="lf-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => upd("phone", e.target.value)}
              placeholder="(555) 555-0110"
              className="mt-1.5 h-11"
              required
            />
          </div>
          <div>
            <Label htmlFor="lf-email" className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
              Email
            </Label>
            <Input
              id="lf-email"
              data-testid="lf-email"
              type="email"
              value={form.email}
              onChange={(e) => upd("email", e.target.value)}
              placeholder="you@email.com"
              className="mt-1.5 h-11"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="lf-addr" className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
            Property Address
          </Label>
          <Input
            id="lf-addr"
            data-testid="lf-address"
            value={form.property_address}
            onChange={(e) => upd("property_address", e.target.value)}
            placeholder="123 Main St, City, ST"
            className="mt-1.5 h-11"
          />
        </div>

        <div>
          <Label className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
            Your Situation *
          </Label>
          <Select value={form.situation} onValueChange={(v) => upd("situation", v)}>
            <SelectTrigger className="mt-1.5 h-11" data-testid="lf-situation">
              <SelectValue placeholder="Select your situation" />
            </SelectTrigger>
            <SelectContent>
              {SITUATIONS.map((s) => (
                <SelectItem key={s.value} value={s.value} data-testid={`lf-situation-${s.value}`}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!compact && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="lf-loan" className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
                  Loan Balance
                </Label>
                <Input
                  id="lf-loan"
                  data-testid="lf-loan-balance"
                  value={form.loan_balance}
                  onChange={(e) => upd("loan_balance", e.target.value)}
                  placeholder="$ approx."
                  className="mt-1.5 h-11"
                />
              </div>
              <div>
                <Label htmlFor="lf-val" className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
                  Estimated Value
                </Label>
                <Input
                  id="lf-val"
                  data-testid="lf-estimated-value"
                  value={form.estimated_value}
                  onChange={(e) => upd("estimated_value", e.target.value)}
                  placeholder="$ approx."
                  className="mt-1.5 h-11"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
                Timeline
              </Label>
              <Select value={form.timeline} onValueChange={(v) => upd("timeline", v)}>
                <SelectTrigger className="mt-1.5 h-11" data-testid="lf-timeline">
                  <SelectValue placeholder="When do you need help?" />
                </SelectTrigger>
                <SelectContent>
                  {TIMELINES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="lf-msg" className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
                Anything we should know?
              </Label>
              <Textarea
                id="lf-msg"
                data-testid="lf-message"
                value={form.message}
                onChange={(e) => upd("message", e.target.value)}
                placeholder="Briefly tell us your situation..."
                className="mt-1.5 min-h-[90px]"
              />
            </div>
          </>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="lf-submit"
          className="mt-2 inline-flex items-center justify-center gap-2 w-full px-8 py-4 rounded-lg bg-[#C5A059] hover:bg-[#B38F4E] disabled:opacity-60 text-[#0B1B3D] font-semibold transition-all shadow-md hover:shadow-lg"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            "Get My Free Rescue Plan"
          )}
        </button>

        <p className="text-[11px] text-[#64748B] text-center leading-relaxed mt-1">
          By submitting, you agree to be contacted by Clear Path Home Relief Services. We never sell your data.
        </p>
      </div>
    </form>
  );
}
